import { businessDate, posSaleDates } from "../../helpers/businessDate";
import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import {
  Input,
  Container,
  Row,
  Col,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Card,
  CardBody,
  Label,
  FormGroup,
  Badge,
  CardHeader,
  Spinner,
  Alert,
} from "reactstrap";

import {
  FaBarcode,
  FaCalendarAlt,
  FaCartPlus,
  FaCreditCard,
  FaEnvelope,
  FaIdCard,
  FaImage,
  FaKeyboard,
  FaList,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaPhone,
  FaReceipt,
  FaSearch,
  FaShoppingCart,
  FaStore,
  FaThLarge,
  FaTrash,
  FaTrashAlt,
  FaUser,
  FaWhatsapp,
  FaBuilding,
  FaDesktop,
  FaSave,
} from "react-icons/fa";

import jsPDF from "jspdf";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import Swal from "sweetalert2";
import { useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";

import emptyCart from "../../assets/images/empty-cart.png";
import config from "../../helpers/config";
import productService from "../../services/productService";
import "./POSPage.css";

import POSHeader from "./POSHeader";
import POSKeypadModal from "./POSKeypadModal";
import POSNumericKeypadModal from "./POSNumericKeypadModal";
import ClientSelectModal from "./ClientSelectModal";

import { productActions } from "../../sagas/productSlice";
import { saleActions } from "../../sagas/saleSlice";
import { clientActions } from "../../sagas/clientSlice";
import { deliveryActions } from "../../sagas/deliveryNoteSlice";
import { transporteurActions } from "../../sagas/transporteurSlice";
import { entrepotActions } from "../../sagas/entrepotSlice";
import { commercialActions } from "../../sagas/commercialSlice";

import { buildEscPosReceipt } from "../../helpers/printUtility";
import { printPdfInHiddenIframe } from "../../helpers/printFrame";

import {
  CBL_PAGE,
  CPMT_PRODUCTS_PAGE,
  CPMT_RECEPTION_PAGE,
  CPMT_STOCK_PAGE,
  PAY_PAGE,
  POS_PAGE,
  RETOUR_FORM_NEW,
  RETOUR_PAGE,
} from "../../routes/routeConstants";
import { shiftActions } from "../../sagas/shiftSlice";
import OpenShiftModal from "./OpenShiftModal";
import CloseShiftModal from "./CloseShiftModal";
import { authActions } from "../../sagas/authSlice";
import {
  createCustomerDisplayChannel,
  DISPLAY_EVENTS,
} from "../../services/customerDisplayChannel";

/* -----------------------------
   FORM INITIAL VALUES + VALIDATION
----------------------------- */

export const createPosInitialValues = (today = businessDate()) => ({
  numero: "",
  commandeId: null,
  commercialId: null,
  commandeNumero: "",
  commandeDateCreation: today,
  clientId: null,
  chantierId: null,
  clientNom: "",
  clientAdresse: "",
  clientIce: "",
  clientTelephone1: "",
  clientTelephone2: "",
  clientEmail: "",
  deliveryNoteNb: "",
  estLivraisonDirecte: false,
  dateLivraison: today,
  lignes: [],
  montantCheque: 0,
  montantTotalHT: 0,
  montantTotalTTC: 0,
  montantTotalTaxe: 0,
  montantPaye: 0,
  montantPayeCash: 0,
  montantPayeCarte: 0,
  montantDroitTimbre: 0,
  addresseLivraison: "",
  modeReglement: "ESPECES",
  dateOperation: today,
  entrepotId: null,
  transporteurId: null,
  transporteurNom: "",
  transporteurTelephone: "",
  status: "EN_ATTENTE",
  accuse: false,
  estFacture: false,
  numeroFacture: "",
  isClientParticulier: false,
  numeroCheque: "",
  banque: "",
  dateEcheanceCheque: null,
});

const validationSchema = Yup.object({
  clientId: Yup.number().nullable(),
  modeReglement: Yup.string()
    .required("Mode de règlement requis")
    .oneOf(
      ["ESPECES", "CHEQUE", "VIREMENT", "TRAITE", "CARTE_BANCAIRE", "EN_COMPTE"],
      "Mode de règlement invalide"
    ),
});

const DROIT_TIMBRE_RATE = 0.0025;
const QUICK_PAYMENT_AMOUNTS = [20, 50, 100, 200, 300, 400, 500];
const BAKERY_BARCODE_PREFIX = "500200";
const MAX_TICKET_ITEMS = 100;
const normalizeClientLabel = (label) => String(label || "").trim().toUpperCase();

const roundCurrency = (amount) => Math.round((Number(amount || 0) + Number.EPSILON) * 100) / 100;

const calculateDroitTimbre = (totalTtc, cardAmount = 0) => {
  const total = Number(totalTtc || 0);
  const card = Math.max(0, Math.min(Number(cardAmount || 0), total));
  const cashBase = Math.max(total - card, 0);
  return roundCurrency(cashBase * DROIT_TIMBRE_RATE);
};

const isBakeryBarcode = (value) =>
  String(value || "").trim().startsWith(BAKERY_BARCODE_PREFIX);

const isBakeryProduct = (product) =>
  [product?.codeBarre, product?.barcode, product?.code].some(isBakeryBarcode);

export const buildCustomerDisplayState = ({
  cart = [],
  totalTTC = 0,
  payment = {},
  paymentActive = false,
  completed = false,
  highlightedArticleId = null,
  shop = {},
  display = {},
}) => {
  const lines = cart.map((item, index) => {
    const quantity = Number(item.quantite || 0);
    const unitPrice = Number(item.prixUnitaireTTC || 0);
    const beforeDiscount = quantity * unitPrice;
    const lineTotal = Number(item.montantTTC ?? beforeDiscount);
    return {
      id: String(item.articleId ?? index),
      designation: item.articleNom || item.libelle || "",
      quantity,
      unitPrice,
      lineTotal,
      discount: Math.max(0, beforeDiscount - lineTotal),
    };
  });
  const subtotal = lines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0);
  const finalAmount = Number(payment.totalDue ?? totalTTC ?? 0);
  const paidAmount = Number(payment.paidAmount || 0);
  return {
    mode: completed ? "COMPLETED" : paymentActive ? "PAYMENT" : lines.length ? "CART" : "WELCOME",
    updatedAt: new Date().toISOString(),
    highlightedLineId: highlightedArticleId == null ? null : String(highlightedArticleId),
    shop,
    display,
    lines,
    totals: {
      subtotal,
      discount: Math.max(0, subtotal - Number(totalTTC || 0)),
      finalAmount,
    },
    payment: {
      paidAmount,
      remainingAmount: Math.max(0, finalAmount - paidAmount),
      receivedAmount: Number(payment.receivedAmount ?? paidAmount),
      changeAmount: Math.max(0, Number(payment.receivedAmount ?? paidAmount) - finalAmount),
    },
  };
};

const SaleForm = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const location = useLocation();
  const [editContext, setEditContext] = useState(() => ({
    editMode: Boolean(location.state?.editMode && location.state?.deliveryNote?.id),
    deliveryNote: location.state?.deliveryNote || null,
  }));
  const editMode = editContext.editMode;
  const deliveryNote = editContext.deliveryNote;

  /* -----------------------------
     STORE SELECTORS
  ----------------------------- */
  const montantTotalTTC = useSelector((state) => state.sales?.montantTotalTTC || 0);
  const montantTotalHT = useSelector((state) => state.sales?.montantTotalHT || 0);
  const montantTotalTaxe = useSelector((state) => state.sales?.montantTotalTaxe || 0);
  const cart = useSelector((state) => state.sales?.cart || []);
  const { currentShift, loading: shiftLoading, error: shiftError } = useSelector(
    (state) => state.shift
  );
  const role = useSelector((state) => state.auth.role);
  const { businessName, email, address, telephone, fullName, commercialId, pointOfSaleId, company } = useSelector((state) => state.auth);
  const [searchMode, setSearchMode] = useState("ALL");
  const [searchModeModal, setSearchModeModal] = useState(false);
  const SEARCH_MODES = [
    { value: "ALL", label: "Tout" },
    { value: "LIBELLE", label: "Libellé" },
    { value: "CODE", label: "Code article" },
    { value: "BARCODE", label: "Code barre" },
  ];

  const {
    products,
    totalPages,
    totalElements,
    pageSize,
  } = useSelector((state) => state.products);

  const unites = useSelector((state) => state.products?.unites || []);
  const clients = useSelector((state) => state.clients?.clients || []);
  const { transporteurs } = useSelector((state) => state.transporteurs || {});
  const { entrepots } = useSelector((state) => state.entrepots || {});
  const { list: commercialList } = useSelector((state) => state.commercials || {});
  const { loading: deliveryLoading } = useSelector((state) => state.deliveryNotes || {});
  const [openShiftModal, setOpenShiftModal] = useState(false);

  const isAdmin = role === "ROLE_ADMIN";
  const canCreateSales = !isAdmin;

  const safeProducts = useMemo(() => products || [], [products]);

  /* -----------------------------
     LOCAL STATE
  ----------------------------- */
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [modal, setModal] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [isSavingSale, setIsSavingSale] = useState(false);
  const [clearCartModal, setClearCartModal] = useState(false);
  const toggleClearCartModal = () => setClearCartModal((v) => !v);

  const [receiptData, setReceiptData] = useState(null);
  const [clientErrors, setClientErrors] = useState({});
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [keypadTarget, setKeypadTarget] = useState(null);

  const [displayMode, setDisplayMode] = useState("table"); // 'card' | 'table'

  const [numPadOpen, setNumPadOpen] = useState(false);
  const [payKeypadOpen, setPayKeypadOpen] = useState(false);
  const customerDisplayChannelRef = useRef(null);
  const customerDisplayStateRef = useRef(null);
  const previousDisplayCartRef = useRef([]);
  const highlightedArticleRef = useRef(null);
  const suppressNextEmptyDisplayRef = useRef(false);

  const [numPadConfig, setNumPadConfig] = useState({
    title: "",
    value: "",
    index: null,
    field: null,
  });

  const [showKeypad, setShowKeypad] = useState(false);

  const [clientSelectModal, setClientSelectModal] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);

  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedCommercial, setSelectedCommercial] = useState(null);
  const [selectedEntrepot, setSelectedEntrepot] = useState(null);
  const [selectedTransporteur, setSelectedTransporteur] = useState(null);
  const [selectedChantier, setSelectedChantier] = useState(null);
  const isEdit = useRef(false);
  const [client, setClient] = useState({
    nom: '',
    prenom: '',
    cin: '',
    telephone1: '',
    telephone2: '',
    email: '',
    adresse: '',
    ville: 'Casablanca',
    typeClient: 'PARTICULIER',
    codePostal: '',
    plafond: 0,
    entreprise: '', // New field for "Entreprise"
    ice: '',// New field for "ICE",
    active: true,
    solde: 0 // Ce champ sera en lecture seule
  });

  const isKeyboardLocked =
    showKeypad ||
    paymentModal ||
    numPadOpen ||
    keypadOpen ||
    payKeypadOpen ||
    clientSelectModal ||
    showClientModal;

  /* -----------------------------
     REFS
  ----------------------------- */
  const searchInputRef = useRef(null);
  const scanBufferRef = useRef("");
  const scanTimerRef = useRef(null);
  const formikRef = useRef(null);

  const productsRef = useRef([]);

  const isShiftBlocked = !shiftLoading && (!currentShift || currentShift.status !== "OPENED");
  const canEditBL = (!editMode || deliveryNote?.status === "EN_PREPARATION") && !isShiftBlocked;

  useEffect(() => {
    productsRef.current = safeProducts;
  }, [safeProducts]);

  const wasKeyboardLockedRef = useRef(false);
  useEffect(() => {
    if (isKeyboardLocked) {
      wasKeyboardLockedRef.current = true;
      searchInputRef.current?.blur();
      return;
    }

    // Restore focus only when we are exiting a locked state
    if (!wasKeyboardLockedRef.current) return;
    wasKeyboardLockedRef.current = false;

    const id1 = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
    // Bootstrap modal/focus-trap can steal focus after close animation; enforce once more.
    const id2 = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 220);

    return () => {
      clearTimeout(id1);
      clearTimeout(id2);
    };
  }, [isKeyboardLocked]);


  useEffect(() => {
    return () => dispatch(saleActions.clearCart());
  }, []);

  const currentSearchModeLabel = SEARCH_MODES.find((m) => m.value === searchMode)?.label || "Tout";

  /* -----------------------------
     UTILITIES
  ----------------------------- */
  const formatPrice = (amount, currency = true) => {
    const formatted = new Intl.NumberFormat("fr-MA", {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount || 0));

    return currency ? `${formatted} DH` : formatted;
  };

  const showToast = (icon, title) => {
    Swal.fire({
      toast: true,
      position: "top-end",
      icon,
      title,
      showConfirmButton: false,
      timer: 1800,
      timerProgressBar: true,
    });
  };

  const getReceiptPaymentSummary = (data) => {
    const total = Number(data?.montantTotalTTC || 0);
    const cash = Number(data?.montantKeptCash ?? data?.montantPayeCash ?? data?.montantPayeEspece ?? 0);
    const card = Number(data?.montantPayeCarte || 0);
    const stampDuty = Number(data?.montantDroitTimbre || 0);
    const totalDue = total + stampDuty;
    const explicitPaid = Number(data?.montantPaye || 0);
    const computedPaid = cash + card;
    const paid = explicitPaid > 0 ? Math.max(explicitPaid, computedPaid) : computedPaid;
    const change = Math.max(paid - totalDue, 0);
    return { total, cash, card, paid, change, stampDuty, totalDue };
  };


  const buildSearchQuery = (term, mode) => {
    const q = (term || "").trim();
    if (!q) return "";

    return q;
  };

  const parseDibalWeightBarcode = (rawCode) => {
    const digits = String(rawCode || "").replace(/\D/g, "");
    if (!digits) return null;

    // Some scanners prepend a leading 0 (EAN-13 wrapper).
    const normalizedDigits =
      digits.length === 13 && digits.startsWith("0") ? digits.slice(1) : digits;

    // Dibal variable-weight format:
    // PPPPPPP + WWWWW + C
    // PPPPPPP: product code (7 digits), WWWWW: grams, C: checksum.
    // Some setups use 21 as a prefix and a 5-digit product code, so keep it
    // as a fallback candidate instead of forcing slice(2, 7).
    if (normalizedDigits.length === 13 && normalizedDigits.startsWith("21")) {
      const productCode = normalizedDigits.slice(0, 7);
      const legacyProductCode = normalizedDigits.slice(2, 7);
      const weightGrams = Number(normalizedDigits.slice(7, 12));
      if (Number.isFinite(weightGrams) && weightGrams > 0) {
        return {
          productCode,
          productCodes: [productCode, legacyProductCode].filter(Boolean),
          weightKg: weightGrams / 1000,
        };
      }
    }

    // Legacy variant:
    // 21 + PPPP + WWWWW + C
    if (normalizedDigits.length === 12 && normalizedDigits.startsWith("21")) {
      const productCode = normalizedDigits.slice(2, 6);
      const weightGrams = Number(normalizedDigits.slice(6, 11));
      if (Number.isFinite(weightGrams) && weightGrams > 0) {
        return {
          productCode,
          productCodes: [productCode],
          weightKg: weightGrams / 1000,
        };
      }
    }

    // Fallback variant used by some labels:
    // 2 + PPPP + WWWWW + C
    if (normalizedDigits.length === 11 && normalizedDigits.startsWith("2")) {
      const productCode = normalizedDigits.slice(1, 5);
      const weightGrams = Number(normalizedDigits.slice(5, 10));
      if (Number.isFinite(weightGrams) && weightGrams > 0) {
        return {
          productCode,
          productCodes: [productCode],
          weightKg: weightGrams / 1000,
        };
      }
    }

    return null;
  };

  const isSameCode = (a, b) => {
    const left = String(a || "").trim().toLowerCase();
    const right = String(b || "").trim().toLowerCase();
    if (!left || !right) return false;
    if (left === right) return true;
    return left.replace(/^0+/, "") === right.replace(/^0+/, "");
  };


  const getModeReglementLabel = (mode) => {
    switch (mode) {
      case "ESPECES":
        return "Espèces";
      case "CARTE_BANCAIRE":
        return "Carte bancaire";
      case "CHEQUE":
        return "Chèque";
      case "VIREMENT":
        return "Virement";
      case "EN_COMPTE":
        return "En compte";
      case "TRAITE":
        return "Traite";
      default:
        return mode || "-";
    }
  };


  const getTotalQuantitiesFromReceipt = (data) => {
    if (!data?.lignes?.length) return 0;
    return data.lignes.reduce((sum, l) => sum + Number(l.quantiteLivree || 0), 0);
  };

  const resetSearchAfterScan = () => {
    setSearchTerm("");
    dispatch(productActions.findAll({
      search: "",
      page: 0,
      size: 200,
    }));
  };

  const playBeep = (type = "success") => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.connect(gain);
      gain.connect(ctx.destination);

      oscillator.frequency.value = type === "error" ? 220 : 880;
      gain.gain.value = 0.12;

      oscillator.start();
      oscillator.stop(ctx.currentTime + (type === "error" ? 0.25 : 0.1));
    } catch (e) {
      // ignore
    }
  };

  /* -----------------------------
     FETCH INITIAL DATA
  ----------------------------- */
  useEffect(() => {
    dispatch(clientActions.search({ query: "" }));
    dispatch(productActions.findAllUnites());
    dispatch(transporteurActions.list());
    dispatch(entrepotActions.list());
    dispatch(commercialActions.list({ query: "" }));
  }, [dispatch]);

  /* -----------------------------
     FETCH PRODUCTS ON PAGE CHANGE
  ----------------------------- */
  useEffect(() => {
    dispatch(
      productActions.findAll({
        search: buildSearchQuery(searchTerm, searchMode),
        page: currentPage - 1,
        size: 200,
      })
    );
  }, [dispatch, currentPage, searchTerm, searchMode]);

  /* -----------------------------
     KEEP SEARCH INPUT FOCUSED
  ----------------------------- */
  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  /* -----------------------------
     CLIENT OPTIONS
  ----------------------------- */
  const clientOptions = useMemo(() => {
    const options = (clients || []).map((c) => ({
      solde: c.solde,
      totalDebit: c.totalDebit,
      totalCredit: c.totalCredit,
      chantiers: c.chantiers,
      value: c.id,
      label: c.typeClient === "PARTICULIER" ? [c.prenom, c.nom].filter(Boolean).join(" ") : c.entreprise,
    })).filter((c) => normalizeClientLabel(c.label) !== "PASSAGER");
    return options;

  }, [clients]);

  const defaultClient = clientOptions.find((c) => normalizeClientLabel(c.label) === "CUSTOMER") || null;

  useEffect(() => {
    setSelectedClient((current) => current && normalizeClientLabel(current.label) !== "PASSAGER" ? current : defaultClient);
  }, [defaultClient]);

  const updatePaymentAmounts = (changedField, value) => {
    const val = Number(value) || 0;
    const currentCard = Number(formik.values.montantPayeCarte) || 0;
    const total = Number(montantTotalTTC) || 0;

    if (changedField === "cash") {
      const stampDuty = calculateDroitTimbre(total, currentCard);
      formik.setFieldValue("montantPayeCash", val);
      formik.setFieldValue("montantDroitTimbre", stampDuty);
      formik.setFieldValue("montantPaye", val + currentCard);
    }

    if (changedField === "card") {
      const cappedCard = Math.max(0, Math.min(val, total));
      const autoCash = Math.max(total - cappedCard, 0);
      const stampDuty = calculateDroitTimbre(total, cappedCard);
      const cashToCollect = autoCash + stampDuty;
      formik.setFieldValue("montantPayeCarte", cappedCard);
      formik.setFieldValue("montantPayeCash", roundCurrency(cashToCollect));
      formik.setFieldValue("montantDroitTimbre", stampDuty);
      formik.setFieldValue("montantPaye", roundCurrency(cashToCollect + cappedCard));
    }
  };

  const handleQuickCashAmount = (amount) => {
    const cashAmount = Number(amount || 0);
    const stampDuty = calculateDroitTimbre(montantTotalTTC, 0);

    formik.setFieldValue("modeReglement", "ESPECES");
    formik.setFieldValue("montantPayeCash", cashAmount);
    formik.setFieldValue("montantPayeCarte", 0);
    formik.setFieldValue("montantDroitTimbre", stampDuty);
    formik.setFieldValue("montantPaye", cashAmount);
  };

  const currentCartItems = cart;

  /* -----------------------------
     CART ACTIONS
  ----------------------------- */
  const handleAddToCart = useCallback(
    (product, options = {}) => {
      if (!canEditBL) {
        Swal.fire({
          icon: "warning",
          title: "Modification non autorisée",
          text: "Ce bon de livraison n'est plus modifiable.",
        });
        return;
      }

      const alreadyInCart = cart.some((item) => String(item.articleId) === String(product?.id));
      if (!alreadyInCart && cart.length >= MAX_TICKET_ITEMS) {
        Swal.fire({
          icon: "warning",
          title: "Ticket plein",
          text: `Un ticket ne peut pas contenir plus de ${MAX_TICKET_ITEMS} articles.`,
        });
        return;
      }

      dispatch(saleActions.addToCart(product));
      if (!options.keepSearch && !isBakeryProduct(product)) {
        setSearchTerm("");
      }
    },
    [canEditBL, cart, dispatch]
  );

  const handleInputChange = useCallback(
    (index, event) => {
      const { name, value } = event.target;
      dispatch(saleActions.handleInputChange({ index, name, value }));
    },
    [dispatch]
  );

  const openQuantityKeypad = useCallback(
    (item, index) => {
      if (!canEditBL) return;
      setNumPadConfig({
        title: `Quantité - ${item.articleNom}`,
        value: item.quantite,
        index,
        field: "quantite",
      });
      setNumPadOpen(true);
    },
    [canEditBL]
  );

  const handleRemoveFromCart = useCallback(
    (productId) => {
      dispatch(saleActions.removeFromCart(productId));
    },
    [dispatch]
  );

  const handleClearCart = () => {
    dispatch(saleActions.clearCart());
    toggleClearCartModal();
  };

  const resetSaleState = useCallback(() => {
    dispatch(saleActions.clearCart());
    setEditContext({ editMode: false, deliveryNote: null });
    navigate(location.pathname, { replace: true, state: null });
    setPaymentModal(false);
    setModal(false);
    setReceiptData(null);
    setSelectedClient(defaultClient);
    setSelectedCommercial(null);
    setSelectedEntrepot(null);
    setSelectedTransporteur(null);
    setSelectedChantier(null);
    formikRef.current?.resetForm?.({ values: createPosInitialValues() });
    setSearchTerm("");
    setCurrentPage(1);

    dispatch(
      productActions.findAll({
        search: "",
        page: 0,
        size: 200,
      })
    );

  }, [defaultClient, dispatch, location.pathname, navigate]);

  /* -----------------------------
     DEBOUNCED SEARCH (FIXED)
  ----------------------------- */
  const debouncedSearchRef = useRef(
    debounce((term, page, mode) => {
      const search = buildSearchQuery(term, mode);

      dispatch(
        productActions.findAll({
          search,
          page,
          size: 200,
        })
      );
    }, 700)
  );

  useEffect(() => {
    return () => {
      // cleanup debounce on unmount
      debouncedSearchRef.current?.cancel?.();
    };
  }, []);

  const tryAddProductByBarcode = useCallback(
    async (rawCode) => {
      const barcode = (rawCode || "").trim();
      if (!barcode) return false;
      const keepSearch = isBakeryBarcode(barcode);
      try {
        const dibalData = parseDibalWeightBarcode(barcode);
        const searchKeys = dibalData?.productCodes?.length ? dibalData.productCodes : [barcode];
        const normalizedSearchKeys = searchKeys.map((key) => String(key || "").trim().toLowerCase()).filter(Boolean);
        const localItems = productsRef.current || [];

        if (dibalData?.productCode) {
          dispatch(
            productActions.findAll({
              search: dibalData.productCode,
              page: 0,
              size: 200,
            })
          );
        }

        const matchByCode = (p) =>
          normalizedSearchKeys.some((normalized) =>
            [p.codeBarre, p.barcode, p.code].some((v) => isSameCode(v, normalized))
          );

        let product = localItems.find(matchByCode);

        for (const searchKey of searchKeys) {
          if (product) break;
          const data = await productService.findAll(searchKey, 0, 50);
          const items = data?.body?.content || data?.content || [];
          product = items.find(matchByCode) || items[0];
        }

        if (!product?.id) return false;
        if (!canEditBL) {
          handleAddToCart(product, { keepSearch });
          return true;
        }

        setSearchMode("BARCODE");
        setSearchTerm(barcode);

        if (dibalData?.weightKg) {
          const existingIndex = cart.findIndex(
            (it) => String(it.articleId) === String(product.id)
          );
          const previousQty =
            existingIndex >= 0 ? Number(cart[existingIndex].quantite) || 0 : 0;

          handleAddToCart(product, { keepSearch });

          const targetIndex = 0;
          const weightedQty = Number((previousQty + dibalData.weightKg).toFixed(3));

          dispatch(
            saleActions.handleInputChange({
              index: targetIndex,
              name: "quantite",
              value: weightedQty,
            })
          );
        } else {
          handleAddToCart(product, { keepSearch });
        }

        if (dibalData?.productCode && !keepSearch) {
          resetSearchAfterScan();
        }

        playBeep("success");
        return true;
      } catch (e) {
        return false;
      }
    },
    [canEditBL, cart, dispatch, handleAddToCart]
  );

  /* -----------------------------
     BARCODE SCAN HANDLING
  ----------------------------- */
  const handleBarcodeScan = useCallback(
    async (code) => {
      // stop scan when keypad/modals open
      if (numPadOpen || showClientModal || clientSelectModal || paymentModal || payKeypadOpen) return;

      const barcode = (code || "").trim();
      if (!barcode) return;

      const added = await tryAddProductByBarcode(barcode);
      if (added) return;

      playBeep("error");
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: "Article introuvable",
        text: `Code: ${barcode}`,
        showConfirmButton: false,
        timer: 1600,
        timerProgressBar: true,
      });
    },
    [
      numPadOpen,
      showClientModal,
      clientSelectModal,
      paymentModal,
      payKeypadOpen,
      tryAddProductByBarcode,
    ]
  );

  // KEYDOWN SCAN EFFECT (FIXED DEP)
  useEffect(() => {
    const flushScanBuffer = () => {
      const scanned = (scanBufferRef.current || "").trim();
      scanBufferRef.current = "";
      if (scanned.length >= 4) {
        handleBarcodeScan(scanned);
      }
    };

    const handleKeyDown = (e) => {
      // Do not hijack keyboard events when any POS modal/keypad is open
      if (numPadOpen || showClientModal || clientSelectModal || paymentModal || payKeypadOpen || keypadOpen || showKeypad) {
        return;
      }

      const key = typeof e?.key === "string" ? e.key : "";

      if (scanTimerRef.current) clearTimeout(scanTimerRef.current);

      if (key === "Enter" || key === "Tab") {
        e.preventDefault();
        e.stopPropagation();
        flushScanBuffer();
        return;
      }

      const activeEl = document.activeElement;
      const tag = activeEl?.tagName?.toLowerCase();
      const isTyping = tag === "input" || tag === "textarea" || activeEl?.isContentEditable;
      if (isTyping) return;

      if (!key || key.length !== 1) {
        scanTimerRef.current = setTimeout(() => {
          scanBufferRef.current = "";
        }, 80);
        return;
      }

      scanBufferRef.current = (scanBufferRef.current || "") + key;

      scanTimerRef.current = setTimeout(() => {
        flushScanBuffer();
      }, 80);
    };

    window.addEventListener("keydown", handleKeyDown, { passive: false });
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleBarcodeScan, numPadOpen, showClientModal, clientSelectModal, paymentModal, payKeypadOpen, keypadOpen, showKeypad]);


  /* -----------------------------
     FORMIK
  ----------------------------- */
  const formik = useFormik({
    initialValues: createPosInitialValues(),
    validationSchema,
    onSubmit: (values) => {
      handleCreateSale(values);
    },
  });
  formikRef.current = formik;

  const handleKeypadConfirm = (value) => {
    if (keypadTarget === "cash") {
      updatePaymentAmounts("cash", value);
    }

    if (keypadTarget === "card") {
      updatePaymentAmounts("card", value);
    }
  };

  const currentPaymentSummary = getReceiptPaymentSummary({
    montantTotalTTC,
    montantPaye: formik.values.montantPaye,
    montantPayeCash: formik.values.montantPayeCash,
    montantPayeCarte: formik.values.montantPayeCarte,
    montantDroitTimbre: formik.values.montantDroitTimbre,
  });
  const remainingPaymentAmount = roundCurrency(
    Math.max(Number(currentPaymentSummary.totalDue || 0) - Number(formik.values.montantPaye || 0), 0)
  );
  const isPaymentInsufficient = paymentModal && remainingPaymentAmount > 0;

  const customerDisplayShop = useMemo(() => {
    const rawLogo = company?.logo || "";
    return {
      name: company?.name || businessName || "MATJARPOS",
      logo: rawLogo?.startsWith("/uploads") ? `${config.baseURL}${rawLogo}` : rawLogo,
      address: company?.address || address || "",
      phone: company?.phone1 || telephone || "",
      email: company?.email || email || "",
    };
  }, [address, businessName, company, email, telephone]);

  const customerDisplayConfig = useMemo(() => ({
    locale: process.env.REACT_APP_CUSTOMER_DISPLAY_LOCALE || "fr-MA",
    currency: process.env.REACT_APP_CUSTOMER_DISPLAY_CURRENCY || "MAD",
    welcomeMessage: process.env.REACT_APP_CUSTOMER_DISPLAY_WELCOME || "Bienvenue chez nous",
    promotionImages: (process.env.REACT_APP_CUSTOMER_DISPLAY_PROMOTIONS || "")
      .split(",").map((value) => value.trim()).filter(Boolean),
    qrCode: process.env.REACT_APP_CUSTOMER_DISPLAY_QR_CODE || "",
    confirmationSeconds: Number(process.env.REACT_APP_CUSTOMER_DISPLAY_CONFIRMATION_SECONDS || 8),
  }), []);

  const makeCustomerDisplayState = useCallback((overrides = {}) => buildCustomerDisplayState({
    cart,
    totalTTC: montantTotalTTC,
    payment: {
      totalDue: currentPaymentSummary.totalDue,
      paidAmount: formik.values.montantPaye,
      receivedAmount: formik.values.montantPaye,
    },
    paymentActive: paymentModal,
    highlightedArticleId: highlightedArticleRef.current,
    shop: customerDisplayShop,
    display: customerDisplayConfig,
    ...overrides,
  }), [
    cart,
    customerDisplayConfig,
    customerDisplayShop,
    currentPaymentSummary.totalDue,
    formik.values.montantPaye,
    montantTotalTTC,
    paymentModal,
  ]);

  useEffect(() => {
    const transport = createCustomerDisplayChannel({
      onMessage: (event) => {
        if (event.type === DISPLAY_EVENTS.DISPLAY_STATE_REQUESTED && customerDisplayStateRef.current) {
          customerDisplayChannelRef.current?.publish(
            DISPLAY_EVENTS.DISPLAY_STATE_RESPONSE,
            customerDisplayStateRef.current
          );
        }
      },
    });
    customerDisplayChannelRef.current = transport;
    return () => {
      transport.close();
      customerDisplayChannelRef.current = null;
    };
  }, []);

  useEffect(() => {
    const previous = previousDisplayCartRef.current;
    const modified = cart.find((item) => {
      const old = previous.find((entry) => String(entry.articleId) === String(item.articleId));
      return !old ||
        Number(old.quantite) !== Number(item.quantite) ||
        Number(old.prixUnitaireTTC) !== Number(item.prixUnitaireTTC) ||
        Number(old.montantTTC) !== Number(item.montantTTC);
    });
    highlightedArticleRef.current = modified?.articleId ?? highlightedArticleRef.current;
    previousDisplayCartRef.current = cart.map((item) => ({ ...item }));

    if (!cart.length && suppressNextEmptyDisplayRef.current) {
      suppressNextEmptyDisplayRef.current = false;
      return;
    }

    const state = makeCustomerDisplayState({
      highlightedArticleId: modified?.articleId ?? highlightedArticleRef.current,
    });
    customerDisplayStateRef.current = state;
    const event = !cart.length
      ? DISPLAY_EVENTS.CART_CLEARED
      : paymentModal
        ? DISPLAY_EVENTS.PAYMENT_UPDATED
        : DISPLAY_EVENTS.CART_UPDATED;
    customerDisplayChannelRef.current?.publish(event, state);
  }, [
    cart,
    formik.values.montantPaye,
    formik.values.montantPayeCarte,
    formik.values.montantPayeCash,
    makeCustomerDisplayState,
    paymentModal,
  ]);

  useEffect(() => {
    if (!paymentModal || !cart.length) return;
    const state = makeCustomerDisplayState({ paymentActive: true });
    customerDisplayStateRef.current = state;
    customerDisplayChannelRef.current?.publish(DISPLAY_EVENTS.PAYMENT_STARTED, state);
  }, [paymentModal]);

  const publishPaymentCompleted = useCallback((values) => {
    const receivedAmount = Number(values.montantPaye || 0);
    const state = makeCustomerDisplayState({
      completed: true,
      paymentActive: false,
      payment: {
        totalDue: currentPaymentSummary.totalDue,
        paidAmount: receivedAmount,
        receivedAmount,
      },
    });
    suppressNextEmptyDisplayRef.current = true;
    customerDisplayStateRef.current = state;
    customerDisplayChannelRef.current?.publish(DISPLAY_EVENTS.PAYMENT_COMPLETED, state);
  }, [currentPaymentSummary.totalDue, makeCustomerDisplayState]);

  /* -----------------------------
     CREATE SALE
  ----------------------------- */
  const handleCreateSale = (values, options = {}) => {
    const { printTicket = false } = options;

    if (deliveryLoading || isSavingSale) return;

    if (!canCreateSales) {
      Swal.fire({
        icon: "info",
        title: "Action non autorisée",
        text: "Le profil administrateur ne peut pas créer de ventes depuis le POS.",
      });
      return;
    }

    if (!values.modeReglement) {
      values.modeReglement = "ESPECES";
    }

    const cardAmount = Number(values.montantPayeCarte || 0);
    const droitTimbre = calculateDroitTimbre(montantTotalTTC, cardAmount);
    const totalToPay = roundCurrency((Number(montantTotalTTC) || 0) + droitTimbre);
    const paidAmount = Number(values.montantPaye) || 0;

    if (values.modeReglement === "ESPECES") {
      if (!paidAmount) {
        Swal.fire({
          icon: "warning",
          title: t("sale.error"),
          text: t("payment.amountRequiredForWalkIn"),
        });
        return;
      }

      if (paidAmount < totalToPay) {
        Swal.fire({
          icon: "warning",
          title: t("sale.error"),
          text: t("payment.amountMustEqualTotal"),
        });
        return;
      }
    }

    // Walk-in validation
    if (!selectedClient) {
      if (values.modeReglement === "EN_COMPTE") {
        Swal.fire({
          icon: "warning",
          title: t("sale.error"),
          text: "Impossible de vendre en compte sans sélectionner un client.",
        });
        return;
      }

      if (values.modeReglement === "ESPECES") {
        if (!values.montantPaye) {
          Swal.fire({
            icon: "warning",
            title: t("sale.error"),
            text: t("payment.amountRequiredForWalkIn"),
          });
          return;
        }

        if (Number(values.montantPaye) < totalToPay) {
          Swal.fire({
            icon: "warning",
            title: t("sale.error"),
            text: t("payment.amountMustEqualTotal"),
          });
          return;
        }
      } else {
        // carte/cheque/virement -> montant auto = total
        values.montantPaye = montantTotalTTC;
      }
    }

	    if (!cart?.length) {
	      Swal.fire({
	        icon: "warning",
	        title: t("sale.error"),
	        text: t("cart.empty"),
	      });
	      return;
	    }

	    if (cart.length > MAX_TICKET_ITEMS) {
	      Swal.fire({
	        icon: "warning",
	        title: "Ticket plein",
	        text: `Un ticket ne peut pas contenir plus de ${MAX_TICKET_ITEMS} articles.`,
	      });
	      return;
	    }
	
	    if (!currentShift || currentShift.status !== "OPENED") {
      Swal.fire({
        icon: "warning",
        title: t("sale.error"),
        text: t("shifts.noShifts"),
      });
      return;
    }

    if (currentShift.pointOfSaleId && pointOfSaleId && currentShift.pointOfSaleId !== pointOfSaleId) {
      Swal.fire({
        icon: "warning",
        title: t("sale.error"),
        text: "Le shift ouvert ne correspond pas a ce point de vente.",
      });
      return;
    }

    const saleData = {
      clientId: selectedClient ? selectedClient.value : null,
      chantierId: selectedChantier ? selectedChantier.value : null,
      entrepotId: selectedEntrepot ? selectedEntrepot.value : null,
      commercialId: selectedCommercial ? selectedCommercial.value : null,
      shiftId: currentShift?.id,
      isComptoir: true,
      isPassager: selectedClient ? false : true,
      transporteurId: selectedTransporteur ? selectedTransporteur.value : null,
      modeReglement: values.modeReglement,
      ...posSaleDates(editMode ? deliveryNote : null),
      montantTotalHT,
      montantTotalTTC,
      montantTotalTaxe,
      montantPaye: montantTotalTTC,
      montantPayeEspece: values.montantPayeCash,
      montantPayeCash: values.montantPayeCash,
      montantPayeCarte: values.montantPayeCarte,
      montantDroitTimbre: droitTimbre,
      status: "LIVRE",
      lignes: cart.map((item) => ({
        articleId: item.articleId,
        articleCode: item.articleCode,
        articleNom: item.articleNom,
        quantiteLivree: item.quantite,
        unite: unites.find((u) => u.code === item.unite)?.libelle || item.unite || "",
        prixUnitaireTTC: item.prixUnitaireTTC,
        prixUnitaireHT: item.prixUnitaireHT,
        montantHT: item.montantHT,
        montantTTC: item.montantTTC,
        tauxTVA: item.tauxTVA,
      })),
      numeroCheque: values.modeReglement === "CHEQUE" ? values.numeroCheque : null,
      banque: values.modeReglement === "CHEQUE" ? values.banque : null,
      dateEcheanceCheque: values.modeReglement === "CHEQUE" ? values.dateEcheanceCheque : null,
      referenceVirement: values.modeReglement === "VIREMENT" ? values.referenceVirement : null,
    };

    if (editMode && deliveryNote?.id) {
      setIsSavingSale(true);
      dispatch(deliveryActions.update({
        id: deliveryNote.id,
        data: saleData,
        onSuccess: async (data) => {
          setIsSavingSale(false);
          showToast("success", t("common.updated"));
          publishPaymentCompleted(values);
          resetSaleState();
          if (printTicket) {
            await generatePDF("THERMIQUE", data);
          }
        },
        onError: (error) => {
          setIsSavingSale(false);
          Swal.fire({
            icon: "error",
            title: t("sale.error"),
            text: error?.message || t("sale.errorMessage"),
          });
        },
      }));
    } else {
      setIsSavingSale(true);
      dispatch(
        deliveryActions.add({
          data: saleData,
          onSuccess: async (data) => {
            setIsSavingSale(false);
            const receiptPayload = {
              ...data,
              montantPaye: values.montantPaye,
              montantPayeCash: values.montantPayeCash,
              montantPayeCarte: values.montantPayeCarte,
              montantDroitTimbre: droitTimbre,
            };
            setReceiptData(receiptPayload);
            publishPaymentCompleted(values);
            resetSaleState();
            showToast("success", t("bonLivraison.savedSuccess"));

            if (printTicket) {
              await generatePDF("THERMIQUE", receiptPayload);
            }
          },
          onError: (error) => {
            setIsSavingSale(false);
            Swal.fire({
              icon: "error",
              title: t("sale.error"),
              text: error?.message || t("sale.errorMessage"),
            });
          },
        })
      );
    }
  };

  const handleSaveDraft = (values) => {
    if (deliveryLoading || isSavingSale) return;

    if (!canCreateSales) {
      Swal.fire({
        icon: "info",
        title: "Action non autorisée",
        text: "Le profil administrateur ne peut pas créer de ventes depuis le POS.",
      });
      return;
    }

	    if (!cart?.length) {
	      Swal.fire({
	        icon: "warning",
	        title: t("sale.error"),
	        text: t("cart.empty"),
	      });
	      return;
	    }

	    if (cart.length > MAX_TICKET_ITEMS) {
	      Swal.fire({
	        icon: "warning",
	        title: "Ticket plein",
	        text: `Un ticket ne peut pas contenir plus de ${MAX_TICKET_ITEMS} articles.`,
	      });
	      return;
	    }
	
	    if (!currentShift || currentShift.status !== "OPENED") {
      Swal.fire({
        icon: "warning",
        title: t("sale.error"),
        text: t("shifts.noShifts"),
      });
      return;
    }

    if (currentShift.pointOfSaleId && pointOfSaleId && currentShift.pointOfSaleId !== pointOfSaleId) {
      Swal.fire({
        icon: "warning",
        title: t("sale.error"),
        text: "Le shift ouvert ne correspond pas a ce point de vente.",
      });
      return;
    }

    const saleData = {
      clientId: selectedClient ? selectedClient.value : null,
      shiftId: currentShift?.id,
      isComptoir: true,
      isPassager: selectedClient ? false : true,
      modeReglement: values.modeReglement,
      ...posSaleDates(editMode ? deliveryNote : null),
      montantTotalHT,
      montantTotalTTC,
      montantTotalTaxe,
	      montantPaye: montantTotalTTC,
	      montantPayeCash: values.montantPayeCash,
	      montantPayeCarte: values.montantPayeCarte,
	      montantDroitTimbre: values.montantDroitTimbre || 0,
	      status: "EN_PREPARATION",
      lignes: cart.map((item) => ({
        articleId: item.articleId,
        articleCode: item.articleCode,
        articleNom: item.articleNom,
        quantiteLivree: item.quantite,
        unite: unites.find((u) => u.code === item.unite)?.libelle || item.unite || "",
        prixUnitaireTTC: item.prixUnitaireTTC,
        prixUnitaireHT: item.prixUnitaireHT,
        montantHT: item.montantHT,
        montantTTC: item.montantTTC,
        tauxTVA: item.tauxTVA,
      })),
    };

    if (editMode && deliveryNote?.id) {
      setIsSavingSale(true);
      dispatch(deliveryActions.update({
        id: deliveryNote.id,
        data: saleData,
        onSuccess: (data) => {
          setIsSavingSale(false);
          showToast("success", t("common.updated"));
          resetSaleState();
        },
        onError: (error) => {
          setIsSavingSale(false);
          Swal.fire({
            icon: "error",
            title: t("sale.error"),
            text: error?.message || t("sale.errorMessage"),
          });
        },
      }));
    } else {
      setIsSavingSale(true);
      dispatch(
        deliveryActions.add({
          data: saleData,
          onSuccess: (data) => {
            setIsSavingSale(false);
            showToast("success", t("bonLivraison.savedSuccess"));
            resetSaleState();
          },
          onError: (error) => {
            setIsSavingSale(false);
            Swal.fire({
              icon: "error",
              title: t("sale.error"),
              text: error?.message || t("sale.errorMessage"),
            });
          },
        })
      );
    }
  };

  /* -----------------------------
     PRINT UTILITIES
  ----------------------------- */
  const printEscPosReceipt = (data) => {
    const escpos = buildEscPosReceipt(data);
    const encoded = encodeURIComponent(escpos);
    window.location.href = `rawbt://print?text=${encoded}`;
  };

  const generateThermiquePDF = async (ticketData = receiptData) => {
    try {
      const measureDoc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [80, 350],
      });
      const estimatedAddressHeight = address
        ? measureDoc.splitTextToSize(address, 72).slice(0, 3).length * 3.3
        : 0;
      const estimatedItemsHeight = (ticketData?.lignes || []).reduce((height, item) => {
        const nameLines = measureDoc.splitTextToSize(item.articleNom || "-", 38);
        return height + 3.8 + Math.max(nameLines.length - 1, 0) * 3.4;
      }, 0);
	      const payment = getReceiptPaymentSummary(ticketData);
	      const receiptHeight = Math.ceil(
	        265 +
	        estimatedAddressHeight +
	        (telephone ? 3.5 : 0) +
	        estimatedItemsHeight +
	        (payment.stampDuty > 0 ? 4.2 : 0) +
	        (payment.change > 0 ? 4.2 : 0)
	      );
	      const thermalPageHeight = Math.min(Math.max(receiptHeight, 230), 280);
	      const doc = new jsPDF({
	        orientation: "portrait",
	        unit: "mm",
	        format: [80, thermalPageHeight],
	      });

      const pageWidth = 80;
      const margin = 4;
      const contentWidth = pageWidth - margin * 2;
	      const right = pageWidth - margin;
	      let y = 7;
	      const pageHeight = doc.internal.pageSize.getHeight();
	      const bottomMargin = 8;
	
	      const ensureSpace = (height = 0) => {
	        if (y + height <= pageHeight - bottomMargin) return;
	        doc.addPage([pageWidth, pageHeight], "portrait");
	        y = 7;
	      };
	
	      const drawRule = (bold = false) => {
	        ensureSpace(bold ? 4 : 3.5);
	        doc.setDrawColor(0);
	        doc.setLineWidth(bold ? 0.35 : 0.12);
	        doc.line(margin, y, right, y);
        y += bold ? 3.6 : 3;
      };
	
	      const drawRow = (label, value, { bold = false, size = 9.5 } = {}) => {
	        ensureSpace(size >= 11 ? 5.5 : 4.7);
	        doc.setFont("helvetica", bold ? "bold" : "normal");
	        doc.setFontSize(size);
	        doc.text(label, margin, y);
        doc.text(formatPrice(value, false), right, y, { align: "right" });
        y += size >= 11 ? 5 : 4.2;
      };
	
	      const drawInfo = (label, value) => {
	        ensureSpace(4.2);
	        doc.setFont("helvetica", "normal");
	        doc.setFontSize(8.2);
	        doc.text(`${label}:`, margin, y);
        doc.text(String(value || "-"), margin + 15, y, { maxWidth: contentWidth - 15 });
        y += 3.8;
      };

      const totalQuantity = (ticketData?.lignes || []).reduce(
        (sum, item) => sum + Number(item.quantiteLivree || 0),
        0
      );

      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.text(businessName || "MATJARPOS", pageWidth / 2, y, { align: "center" });
      y += 5.5;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      if (address) {
        const lines = doc.splitTextToSize(address, contentWidth);
        lines.slice(0, 3).forEach((line) => {
          doc.text(line, pageWidth / 2, y, { align: "center" });
          y += 3.3;
        });
      }
      if (telephone) {
        doc.text(`Tel: ${telephone}`, pageWidth / 2, y, { align: "center" });
        y += 3.5;
      }

      drawRule(true);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("TICKET DE CAISSE", pageWidth / 2, y, { align: "center" });
      y += 5;

      drawInfo("Ticket", ticketData?.numero);
      drawInfo("Date", ticketData?.createdAt || ticketData?.dateOperation);
      drawInfo("Client", ticketData?.clientNom || "Passager");
      //if (ticketData?.createdBy) drawInfo("Caissier", ticketData.createdBy);

      drawRule();

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.text("DESIGNATION", margin, y);
      doc.text("QTE", 47, y, { align: "right" });
      doc.text("P.U.", 59, y, { align: "right" });
      doc.text("TOTAL", right, y, { align: "right" });
      y += 3.5;
      drawRule();

      (ticketData?.lignes || []).forEach((item) => {
        const qte = Number(item.quantiteLivree || 0);
	        const pu = Number(item.prixUnitaireTTC || 0);
	        const total = Number(item.montantTTC || qte * pu);
	        const nameLines = doc.splitTextToSize(item.articleNom || "-", 38);
	        ensureSpace(3.8 + Math.max(nameLines.length - 1, 0) * 3.4);
	
	        doc.setFont("helvetica", "bold");
	        doc.setFontSize(8.5);
        doc.text(nameLines[0], margin, y);
        doc.text(String(qte), 47, y, { align: "right" });
        doc.text(formatPrice(pu, false), 59, y, { align: "right" });
        doc.text(formatPrice(total, false), right, y, { align: "right" });
        y += 3.8;

        if (nameLines.length > 1) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          nameLines.slice(1).forEach((line) => {
            doc.text(line, margin + 2, y);
            y += 3.4;
          });
	        }
	      });
	
	      ensureSpace(90);
	      drawRule();
	      doc.setFont("helvetica", "normal");
	      doc.setFontSize(8.5);
      doc.text(`${ticketData?.lignes?.length || 0} article(s)`, margin, y);
      doc.text(`${totalQuantity} piece(s)`, right, y, { align: "right" });
      y += 4.2;
      drawRule(true);

      drawRow("TOTAL TTC", ticketData?.montantTotalTTC || 0, { bold: true, size: 11 });
      if (payment.stampDuty > 0) {
	        drawRow("Droit de timbre 0,25%", payment.stampDuty);
	      }
	
	      ensureSpace(10);
	      doc.setFillColor(0, 0, 0);
	      doc.rect(margin, y - 0.5, contentWidth, 7, "F");
	      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("NET A PAYER", margin + 2, y + 4.2);
      doc.text(formatPrice(payment.totalDue, false), right - 2, y + 4.2, { align: "right" });
	      doc.setTextColor(0, 0, 0);
	      y += 9.5;
	
	      ensureSpace(34);
	      doc.setFont("helvetica", "bold");
	      doc.setFontSize(9.5);
	      doc.text("PAIEMENT", margin, y);
      y += 4.2;
      drawInfo("Mode", getModeReglementLabel(ticketData?.modeReglement));
      drawRow("Especes", payment.cash);
      drawRow("Carte", payment.card);
      drawRow("Montant paye", payment.paid, { bold: true });
      if (payment.change > 0) {
        drawRow("Monnaie", payment.change, { bold: true });
	      }
	
	      ensureSpace(18);
	      drawRule();
	      doc.setFont("helvetica", "normal");
	      doc.setFontSize(8.5);
      doc.text("Merci de votre confiance", pageWidth / 2, y, { align: "center" });
      y += 4;
      doc.text("A bientot", pageWidth / 2, y, { align: "center" });
      y += 4;
      doc.text("cima.ma / @cimamarket.ma", pageWidth / 2, y, { align: "center" });

	      printPdfInHiddenIframe(doc.output("bloburl"));
    } catch (e) {
      console.error(e);
    }
  };

  const generatePDF = async (pdfFormat, ticketData) => {
    if (pdfFormat === "THERMIQUE") {
      const isAndroid = /Android/i.test(navigator.userAgent);
      if (isAndroid) {
        printEscPosReceipt({
          businessName,
          address,
          telephone,
          receiptData: ticketData,
        });
      } else {
        await generateThermiquePDF(ticketData);
      }
    } else {
      await generateThermiquePDF(ticketData);
    }
  };

  const handleClientChange = (e) => {
    const { name, value } = e.target;
    setClient((prev) => ({
      ...prev,
      [name]: name === 'active' ? value === 'true' : value, // Convert "true"/"false" to boolean for the active field
    }));
  };

  const handleSubmitClient = (e) => {
    e.preventDefault();

    const newErrors = {};
    // Common validations
    if (!client.telephone1) newErrors.telephone1 = 'Téléphone obligatoire';
    if (!client.adresse) newErrors.adresse = 'Adresse obligatoire';

    // Validations for "PARTICULIER"
    if (client.typeClient === 'PARTICULIER') {
      if (!client.nom) newErrors.nom = 'Nom obligatoire';
      if (!client.prenom) newErrors.prenom = 'Prénom obligatoire';
    }

    // Validations for "ENTREPRISE"
    if (client.typeClient === 'ENTREPRISE') {
      if (!client.entreprise) newErrors.entreprise = 'Nom de l\'entreprise obligatoire';
      if (!client.ice) newErrors.ice = 'Numéro ICE obligatoire';
    }


    if (Object.keys(newErrors).length > 0) {
      setClientErrors(newErrors);
      return;
    }

    let payload = {
      data: {
        ...client,
        chantiers: [],
      },

      onSuccess: (createdClient) => {
        Swal.fire({
          icon: "success",
          title: "Succès",
          text: "Client enregistré avec succès !",
          timer: 1200,
          showConfirmButton: false,
        });

        // ✅ refresh list from backend
        dispatch(clientActions.search({ query: "" }));

        // ✅ reset form
        setClient({
          nom: "",
          prenom: "",
          cin: "",
          telephone1: "",
          telephone2: "",
          typeClient: "PARTICULIER",
          email: "",
          adresse: "",
          ville: "Casablanca",
          codePostal: "",
          plafond: 0,
          entreprise: "",
          ice: "",
          solde: 0,
          active: true,
        });

        // ✅ close create modal + reopen select modal
        setShowClientModal(false);

        setTimeout(() => {
          setClientSelectModal(true);
        }, 250);
      },
    };

    dispatch(clientActions.addClient(payload));

  }


  useEffect(() => {
    if (editMode && deliveryNote && !isEdit.current) {
      isEdit.current = true;

      // 1️⃣ Fill client
      if (deliveryNote.clientId) {
        const foundClient = clientOptions.find(c => c.value === deliveryNote.clientId);
        if (foundClient) setSelectedClient(foundClient);
      }

      // 2️⃣ Fill commercial
      if (deliveryNote.commercialId) {
        const foundCommercial = commercialList?.find(c => c.id === deliveryNote.commercialId);
        if (foundCommercial) {
          setSelectedCommercial({
            value: foundCommercial.id,
            label: foundCommercial.fullName
          });
        }
      }
      console.log(deliveryNote)
      formik.setFieldValue('montantPayeCash', deliveryNote.montantPayeCash);
      formik.setFieldValue('montantPayeCarte', deliveryNote.montantPayeCarte);
      formik.setFieldValue('montantPaye', deliveryNote.montantPaye);
      formik.setFieldValue('montantDroitTimbre', deliveryNote.montantDroitTimbre || 0);

      // 3️⃣ Fill warehouse
      if (deliveryNote.entrepotId) {
        const foundEntrepot = entrepots?.find(e => e.id === deliveryNote.entrepotId);
        if (foundEntrepot) {
          setSelectedEntrepot({
            value: foundEntrepot.id,
            label: foundEntrepot.nom
          });
        }
      }

      // 4️⃣ Load BL lines into cart
      const cartItems = deliveryNote.lignes.map(l => ({
        articleId: l.articleId,
        articleCode: l.articleCode,
        articleNom: l.articleNom,
        quantite: l.quantiteLivree,
        prixUnitaireTTC: l.prixUnitaireTTC,
        prixUnitaireHT: l.prixUnitaireHT,
        montantHT: l.montantHT,
        montantTTC: l.montantTTC,
        tauxTVA: l.tauxTVA,
        unite: l.unite
      }));
      dispatch(saleActions.setCart(cartItems));
    }
  }, [editMode, deliveryNote, clientOptions, commercialList, entrepots, dispatch]);

  const TableView = () => (
    <>
      <div className="pos-products-container pos-products-panel-list">
        {safeProducts.length === 0 ? (
          <div className="text-muted text-center py-4">{t("product.noProducts")}</div>
        ) : (
          safeProducts.map((product) => {
            const unitLabel = unites.find((u) => u.code === product.unite)?.libelle || "";

            return (
              <div
                key={product.id}
                className="pos-list-product-row"
                onClick={() => handleAddToCart(product)}
              >
                <div className="pos-list-product-main">
                  <div className="fw-bold">{product.libelle || "-"}</div>
                  <div className="text-muted small">
                    Code: {product.codeBarre || "-"} | Stock: {product.stockDisponible ?? 0} | Unit: {unitLabel || "-"}
                  </div>
                </div>
                <div className="pos-list-product-price">
                  {formatPrice(product.prixVenteTTC1 || 0, false)}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="d-flex justify-content-between align-items-center mt-3">
        <small className="text-muted">
          {t("product.showing", {
            start: (currentPage - 1) * pageSize + 1,
            end: Math.min(currentPage * pageSize, totalElements),
            total: totalElements,
            currentPage,
            totalPages,
          })}
        </small>
      </div>
    </>
  );

  const CardView = () => (
    <div className="pos-products-container">
      <Row xs={2} md={3} lg={4} className="g-3">
        {safeProducts.length === 0 && (
          <Col xs={12} className="text-center py-5 text-muted">
            {t("product.noProducts")}
          </Col>
        )}

        {safeProducts.map((product) => {
          const unitLabel = unites.find((u) => u.code === product.unite)?.libelle || "";

          return (
            <Col key={product.id}>
              <div className="pos-product-card" onClick={() => handleAddToCart(product)}>
                <div className="pos-product-image">
                  {product.photo ? (
                    <img
                      src={
                        product.photo.startsWith("http")
                          ? product.photo
                          : `${config.baseURL}${product.photo}`
                      }
                      alt={product.libelle}
                      className="product-img"
                    />
                  ) : (
                    <FaImage size={28} className="text-muted" />
                  )}
                </div>

                <div className="pos-product-body">
                  <div className="pos-product-title">{product.libelle}</div>
                  <div className="pos-product-code">{product.codeBarre}</div>

                  <div className="pos-product-meta">
                    <span className="unit">{unitLabel}</span>

                    <span
                      className={`stock ${product.stockDisponible <= 0 ? "out" : product.stockDisponible < 5 ? "low" : "ok"
                        }`}
                    >
                      {product.stockDisponible}
                    </span>
                  </div>

                  <div className="pos-product-footer">
                    <span className="price">{formatPrice(product.prixVenteTTC1)}</span>

                    <button
                      className="add-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddToCart(product);
                      }}
                      title={t("cart.add")}
                    >
                      <FaCartPlus />
                    </button>
                  </div>
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    </div>
  );


  /* -----------------------------
     RENDER
  ----------------------------- */
  return (
    <Container fluid>
      <POSHeader
        businessName={businessName || "MATJARPOS"}
        fullName={fullName}
        commercialId={commercialId}
        pointOfSaleId={pointOfSaleId}
        isAdmin={isAdmin}
        activeTab={"VENTES"}
        onChangeTab={(tab) => {
          if (tab === "VENTES") navigate(POS_PAGE);
          if (tab === "STOCK") navigate(CPMT_STOCK_PAGE);
          if (tab === "PRODUCTS") navigate(CPMT_PRODUCTS_PAGE);
          if (tab === "AVANCE") navigate(PAY_PAGE);
          if (tab === "RETOURS") navigate(RETOUR_FORM_NEW);
          if (tab === "RECEPTION") navigate(CPMT_RECEPTION_PAGE);
        }}

      />

      <Row className="pos-main-layout g-3">
        {/* LEFT */}
        <Col className="pos-left pos-products-pane">
          <Card className="shadow-sm rounded-4 border-0 h-100">
            <CardBody className="h-100 d-flex flex-column">
              {/* Search */}
              <div className="mb-3 position-relative d-flex gap-2">
                <div className="flex-grow-1 position-relative">
                  <Input
                    innerRef={searchInputRef}
                    type="text"
                    placeholder={t("product.searchFocusHint")}
                    value={searchTerm}
                    onChange={(e) => {
                      if (isKeyboardLocked) return;
                      setSearchTerm(e.target.value);
                    }}
                    onPaste={(e) => {
                      if (isKeyboardLocked) return;
                      const pasted = e.clipboardData?.getData("text") || "";
                      const barcode = pasted.trim();
                      if (barcode.length < 4) return;
                      e.preventDefault();
                      handleBarcodeScan(barcode);
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter") return;
                      e.preventDefault();
                      e.stopPropagation();
                      const term = (searchTerm || "").trim();
                      if (term.length >= 4) {
                        handleBarcodeScan(term);
                        return;
                      }
                      dispatch(productActions.findAll({
                        search: buildSearchQuery(term, searchMode),
                        page: 0,
                        size: 200,
                      }));
                      setCurrentPage(1);
                    }}
                    readOnly={isKeyboardLocked}
                    className="ps-4 pe-5 rounded-3 border border-2"
                  />
                  <FaSearch className="pos-search-icon" />
                  <FaBarcode className="pos-barcode-icon" />
                </div>

                <Button
                  color="light"
                  className="rounded-3 fw-bold border"
                  style={{ minWidth: 120 }}
                  onClick={() => setSearchModeModal(true)}
                  title="Mode de recherche"
                >
                  {currentSearchModeLabel}
                </Button>

                <Button
                  color="secondary"
                  className="rounded-3 fw-bold"
                  style={{ minWidth: 60 }}
                  onClick={() => setShowKeypad(true)}
                  title="Clavier"
                >
                  <FaKeyboard />
                </Button>
              </div>

              <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                <div className="text-muted small">
                  {t("product.showing", {
                    start: totalElements === 0 ? 0 : (currentPage - 1) * pageSize + 1,
                    end: Math.min(currentPage * pageSize, totalElements),
                    total: totalElements,
                    currentPage,
                    totalPages,
                  })}
                </div>

                <div className="pos-view-toggle">
                  <Button
                    color={displayMode === "card" ? "primary" : "light"}
                    className="rounded-3 border d-flex align-items-center gap-2"
                    onClick={() => setDisplayMode("card")}
                  >
                    <FaThLarge />
                    <span>Cards</span>
                  </Button>
                  <Button
                    color={displayMode === "table" ? "primary" : "light"}
                    className="rounded-3 border d-flex align-items-center gap-2"
                    onClick={() => setDisplayMode("table")}
                  >
                    <FaList />
                    <span>List</span>
                  </Button>
                </div>
              </div>

              {/* Products */}
              <div className="flex-grow-1 min-h-0">
                {displayMode === "table" ? <TableView /> : <CardView />}
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* RIGHT */}
        <Col className="pos-right pos-cart-pane">
          {/* CLIENT */}
          <Card className="shadow-sm rounded-4 border-0 mb-3">
            <CardBody>
              <FormGroup className="mb-2">
                <Label className="fw-bold text-secondary d-flex justify-content-between align-items-center">
                  <span>
                    <FaUser className="me-1" /> {t("commande.client")}
                  </span>
                </Label>

                <Button
                  color="light"
                  className="w-100 text-start fw-bold py-2 rounded-3 border"
                  disabled={!canEditBL}
                  onClick={() => setClientSelectModal(true)}
                >
                  {selectedClient ? selectedClient.label : t("common.selectOption")}
                </Button>
              </FormGroup>

              {/* {selectedClient && (
                <div className="pos-client-summary mt-3">
                  <div>
                    <small>Débit</small>
                    <strong className="text-danger">{formatPrice(selectedClient.totalDebit)}</strong>
                  </div>
                  <div>
                    <small>Crédit</small>
                    <strong className="text-success">{formatPrice(selectedClient.totalCredit)}</strong>
                  </div>
                  <div>
                    <small>Solde</small>
                    <strong>{formatClientSolde(selectedClient.totalDebit, selectedClient.totalCredit)}</strong>
                  </div>
                </div>
              )} */}

              <hr />
            </CardBody>
          </Card>

          {/* CART */}
          <Card className="shadow-sm rounded-4 border-0 h-100 pos-cart-card">
            <CardHeader className="bg-light fw-bold d-flex justify-content-between align-items-center flex-shrink-0">
              <span>
                <FaShoppingCart className="me-2" />
                {t("cart.title")}
              </span>

              <div className="d-flex align-items-center gap-2">
                {cart.length > 0 && <Badge color="primary">{cart.length}</Badge>}

                {cart.length > 0 && canEditBL && (
                  <Button color="danger" size="sm" outline onClick={toggleClearCartModal}>
                    <FaTrash />
                  </Button>
                )}
              </div>
            </CardHeader>

            <CardBody className="p-0 d-flex flex-column h-100">
              <div className="pos-cart-items flex-grow-1">
                {cart.length === 0 ? (
                  <div className="text-center text-muted py-4">{t("cart.empty")}</div>
                ) : (
                  currentCartItems.map((item, index) => (
                    <div
                      key={item.articleId}
                      className="pos-cart-line d-flex justify-content-between align-items-center"
                    >
                      <div className="flex-grow-1 me-2">
                        <strong className="d-block">{item.articleNom}</strong>

		                        <div className="d-flex gap-2 mt-1 align-items-center">
		                          {/* Qty */}
		                          <Input
                            type="number"
                            name="quantite"
                            value={item.quantite}
                            inputMode="decimal"
                            step="0.001"
                            min="0"
                            disabled={!canEditBL}
                            className="pos-cart-input"
                            onChange={(e) => handleInputChange(index, e)}
                            onFocus={(e) => e.target.select()}
                            onKeyDown={(e) => {
                              if (e.key !== "Enter") return;
                              e.preventDefault();
                              e.stopPropagation();
                              searchInputRef.current?.focus();
                            }}
		                          />
                              <Button
                                type="button"
                                color="secondary"
                                outline
                                size="sm"
                                disabled={!canEditBL}
                                className="d-inline-flex align-items-center justify-content-center"
                                title="Clavier numérique"
                                onClick={() => openQuantityKeypad(item, index)}
                              >
                                <FaKeyboard />
                              </Button>
		                        </div>
	                        <div className="small text-muted mt-1">
	                          PU: <span className="fw-semibold">{formatPrice(item.prixUnitaireTTC, false)} DH</span> / {item.unite || "-"}
	                        </div>
	                      </div>

                      <div className="text-end d-flex flex-column align-items-end">
                        <strong>{formatPrice(item.montantTTC, false)}</strong>

                        <Button
                          color="danger"
                          size="sm"
                          outline
                          disabled={!canEditBL}
                          className="mt-1"
                          onClick={() => {
                            if (!canEditBL) return;
                            handleRemoveFromCart(item.articleId);
                          }}
                        >
                          <FaTrashAlt />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="pos-cart-footer flex-shrink-0">
                <div className="d-flex justify-content-between fw-bold fs-5">
                  <span>{t("cart.totalTTC")}</span>
                  <span>{formatPrice(montantTotalTTC)}</span>
                </div>

                {/* ACTIONS */}
                <div className="d-flex gap-2 mt-3 flex-wrap">
                  <Button
                    color="primary"
                    outline
                    size="lg"
                    className="fw-bold"
                    onClick={() => window.open(
                      "/customer-display",
                      "matjarpos-customer-display",
                      "popup=yes,width=1280,height=720"
                    )}
                  >
                    <FaDesktop className="me-2" />
                    Ouvrir l’affichage client
                  </Button>
                  {/* 💾 SAVE */}
                  <Button
                    color="warning"
                    size="lg"
                    className="flex-fill fw-bold"
                    disabled={!canEditBL || cart.length === 0 || !canCreateSales || deliveryLoading || isSavingSale}
                    onClick={() => handleSaveDraft(formik.values)}
                  >
                    {deliveryLoading ? <Spinner size="sm" className="me-2" /> : <FaSave className="me-2" />}
                    {t("cart.save")}
                  </Button>

                  {/* 💰 CHECKOUT */}
                  <Button
                    color="success"
                    size="lg"
                    className="flex-fill fw-bold"
                    disabled={!canEditBL || !canCreateSales || isSavingSale}
                    onClick={() => {
                      if (!cart?.length) return;

                      if (!selectedClient) {
                        Swal.fire({
                          icon: "info",
                          title: "Client non sélectionné",
                          text: t("payment.clientRequiredForWalkIn"),
                        });
                        return;
                      }

                      const total = Number(montantTotalTTC) || 0;
                      const stampDuty = calculateDroitTimbre(total, 0);
                      const cashToCollect = roundCurrency(total + stampDuty);
                      formik.setFieldValue("modeReglement", "ESPECES");
                      formik.setFieldValue("montantPayeCash", cashToCollect);
                      formik.setFieldValue("montantPayeCarte", 0);
                      formik.setFieldValue("montantDroitTimbre", stampDuty);
                      formik.setFieldValue("montantPaye", cashToCollect);
                      setPaymentModal(true);
                    }}
                  >
                    {!canCreateSales ? "Création désactivée (Admin)" : canEditBL ? t("cart.checkout") : "BL Verrouillé"}
                  </Button>

                  {!isAdmin && (
                    <Button
                      color="info"
                      outline
                      size="lg"
                      className="fw-bold"
                      onClick={() => navigate(CBL_PAGE)}
                    >
                      {t("common.list", "Liste")}
                    </Button>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal isOpen={searchModeModal} toggle={() => setSearchModeModal(false)} centered>
        <ModalHeader toggle={() => setSearchModeModal(false)}>
          Choisir le mode de recherche
        </ModalHeader>

        <ModalBody>
          <div className="d-grid gap-3">
            {SEARCH_MODES.map((m) => (
              <Button
                key={m.value}
                color={searchMode === m.value ? "primary" : "light"}
                className="py-3 fw-bold"
                onClick={() => {
                  setSearchMode(m.value);
                  setSearchModeModal(false);

                  // reset page + refresh search
                  setCurrentPage(1);
                  debouncedSearchRef.current(searchTerm, 0, m.value);
                }}
              >
                {m.label}
              </Button>
            ))}
          </div>
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={() => setSearchModeModal(false)}>
            Fermer
          </Button>
        </ModalFooter>
      </Modal>


      {/* -----------------------------
         PAYMENT MODAL
      ----------------------------- */}
      <Modal isOpen={paymentModal} toggle={() => setPaymentModal(false)} size="xl">
        <ModalHeader toggle={() => setPaymentModal(false)}>{t("sale.confirmSale")}</ModalHeader>
        <ModalBody>
          <Card className="border-0 shadow-sm bg-light bg-gradient mb-3">
            <CardBody>
              <Row className="mb-3">
                <Col md="6" className="d-flex flex-column justify-content-center">
                  <div className="mb-2 text-muted fw-bold text-uppercase small">
                    <FaMoneyBillWave className="me-2 text-success" /> {t("payment.totalDue")}
                  </div>
                  <div className="fs-3 fw-bold text-success">{formatPrice(currentPaymentSummary.totalDue)}</div>
                  {currentPaymentSummary.stampDuty > 0 && (
                    <div className="small text-muted mt-1">
                      TTC {formatPrice(montantTotalTTC)} + droit de timbre {formatPrice(currentPaymentSummary.stampDuty)}
                    </div>
                  )}
                </Col>

                <Col md="6" className="d-flex flex-column justify-content-center">
                  <div className="mb-2 text-muted fw-bold text-uppercase small">
                    <FaCreditCard className="me-2 text-primary" /> {t("payment.method")}
                  </div>

                  <div className="d-flex flex-wrap gap-2">
                    {/* ESPECES */}
                    <Button
                      color={formik.values.modeReglement === "ESPECES" ? "primary" : "secondary"}
                      onClick={() => {
                        const stampDuty = calculateDroitTimbre(montantTotalTTC, 0);
                        const cashToCollect = roundCurrency(Number(montantTotalTTC || 0) + stampDuty);
                        formik.setFieldValue("modeReglement", "ESPECES");
                        formik.setFieldValue("montantPayeCash", cashToCollect);
                        formik.setFieldValue("montantPayeCarte", 0);
                        formik.setFieldValue("montantDroitTimbre", stampDuty);
                        formik.setFieldValue("montantPaye", cashToCollect);
                      }}
                      className="d-flex align-items-center px-4 py-2"
                    >
                      <FaMoneyBillWave className="me-2" /> Espèces
                    </Button>

                    {/* CARTE */}
                    <Button
                      color={formik.values.modeReglement === "CARTE_BANCAIRE" ? "primary" : "secondary"}
                      onClick={() => {
                        formik.setFieldValue("modeReglement", "CARTE_BANCAIRE");
                        // auto paid full
                        formik.setFieldValue("montantPayeCarte", montantTotalTTC);
                        formik.setFieldValue("montantPayeCash", 0);
                        formik.setFieldValue("montantDroitTimbre", 0);
                        formik.setFieldValue("montantPaye", montantTotalTTC);

                      }}
                      className="d-flex align-items-center px-4 py-2"
                    >
                      <FaCreditCard className="me-2" /> Carte
                    </Button>
                  </div>


                  {formik.touched.modeReglement && formik.errors.modeReglement && (
                    <div className="invalid-feedback d-block mt-2">{formik.errors.modeReglement}</div>
                  )}
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md="4">
                  <FormGroup>
                    <Label className="fw-bold text-muted">
                      <FaMoneyBillWave className="me-2 text-success" />
                      Espèces
                    </Label>
                    <Input
                      type="text"
                      readOnly
                      value={formik.values.montantPayeCash}
                      onClick={() => {
                        setKeypadTarget("cash");
                        setKeypadOpen(true);
                      }}
                      className="py-2 fs-5 fw-bold cursor-pointer"
                    />
                  </FormGroup>
                </Col>

                <Col md="4">
                  <FormGroup>
                    <Label className="fw-bold text-muted">
                      <FaCreditCard className="me-2 text-primary" />
                      Carte
                    </Label>
                    <Input
                      type="text"
                      readOnly
                      value={formik.values.montantPayeCarte}
                      onClick={() => {
                        setKeypadTarget("card");
                        setKeypadOpen(true);
                      }}
                      className="py-2 fs-5 fw-bold cursor-pointer"
                    />
                  </FormGroup>
                </Col>

                <Col md="4">
                  <FormGroup>
                    <Label className="fw-bold text-muted">
                      {t("payment.amountPaid")}
                    </Label>
                    <Input
                      type="number"
                      value={formik.values.montantPaye}
                      readOnly
                      className="bg-light py-2 fs-5 fw-bold text-success"
                    />
                  </FormGroup>
	                </Col>
	              </Row>

	              <div className="mb-3">
	                <Label className="fw-bold text-muted mb-2">
	                  Montant payé
	                </Label>
	                <div className="d-flex flex-wrap gap-2">
	                  {QUICK_PAYMENT_AMOUNTS.map((amount) => (
	                    <Button
	                      key={amount}
	                      type="button"
	                      color={Number(formik.values.montantPayeCash) === amount ? "primary" : "light"}
	                      className="fw-bold px-4 py-2 border"
	                      onClick={() => handleQuickCashAmount(amount)}
	                    >
	                      {amount} DH
	                    </Button>
	                  ))}
	                </div>
	              </div>

	              {isPaymentInsufficient && (
	                <Alert color="warning" className="d-flex align-items-center justify-content-between gap-3 mb-3">
	                  <div>
	                    <strong>Montant insuffisant.</strong>
	                    <span className="ms-2">Reste à payer: {formatPrice(remainingPaymentAmount)}</span>
	                  </div>
	                  <Button
	                    color="warning"
	                    size="sm"
	                    className="fw-bold"
	                    onClick={() => handleQuickCashAmount(currentPaymentSummary.totalDue)}
	                  >
	                    Compléter
	                  </Button>
	                </Alert>
	              )}

	              {Number(formik.values.montantPaye || 0) > Number(currentPaymentSummary.totalDue || 0) && (
	                <Col md="6">
                  <FormGroup>
                    <Label className="fw-bold text-muted">
                      {t("payment.change")}
                    </Label>
                    <Input
                      type="number"
                      value={(
                        Number(formik.values.montantPaye || 0) - Number(currentPaymentSummary.totalDue || 0)
                      ).toFixed(2)}
                      disabled
                      className="bg-light text-success py-2 fs-5"
                    />
                  </FormGroup>
                </Col>
              )}

              {/* {formik.values.modeReglement === "EN_COMPTE" && (
                <Row className="mt-3">
                  <Col md="6">
                    <FormGroup>
                      <Label className="fw-bold text-muted">Note</Label>
                      <Input
                        value={formik.values.notePaiement}
                        onChange={(e) => formik.setFieldValue("notePaiement", e.target.value)}
                        placeholder="Paiement Ã  crÃ©dit / Ã  rÃ©gler plus tard..."
                      />
                    </FormGroup>
                  </Col>
                </Row>
              )} */}

            </CardBody>
          </Card>
        </ModalBody>

        <ModalFooter className="d-flex justify-content-end">
          <Button
            color="primary"
            size="lg"
            className="px-4"
            disabled={!canCreateSales || deliveryLoading || isSavingSale || isShiftBlocked || isPaymentInsufficient}
            onClick={() => handleCreateSale(formik.values, { printTicket: false })}
          >
            {deliveryLoading && <Spinner size="sm" className="me-2" />}
            {t("payment.confirm")}
          </Button>
          <Button
            color="success"
            size="lg"
            className="px-4 ms-2"
            disabled={!canCreateSales || deliveryLoading || isSavingSale || isShiftBlocked || isPaymentInsufficient}
            onClick={() => handleCreateSale(formik.values, { printTicket: true })}
          >
            {deliveryLoading && <Spinner size="sm" className="me-2" />}
            <FaReceipt className="me-2" />
            {t("payment.confirmAndPrint", "Confirmer et imprimer")}
          </Button>
          <Button
            color="secondary"
            size="lg"
            className="px-4 ms-2"
            disabled={deliveryLoading || isSavingSale}
            onClick={() => setPaymentModal(false)}
          >
            {t("payment.cancel")}
          </Button>
        </ModalFooter>
      </Modal>

      {/* CLEAR CART */}
      <Modal isOpen={clearCartModal} toggle={toggleClearCartModal} size="xs">
        <ModalHeader toggle={toggleClearCartModal}>{t("cart.clearConfirmTitle")}</ModalHeader>
        <ModalBody>{t("cart.clearConfirmMessage")}</ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handleClearCart}>
            {t("cart.confirm")}
          </Button>
          <Button color="secondary" onClick={toggleClearCartModal}>
            {t("cart.cancel")}
          </Button>
        </ModalFooter>
      </Modal>

      {/* SEARCH KEYPAD */}
      <POSKeypadModal
        isOpen={showKeypad}
        toggle={() => setShowKeypad(false)}
        value={searchTerm}
        onChange={(val) => {
          setSearchTerm(val);
          debouncedSearchRef.current(val, currentPage - 1, searchMode);
        }}
        onSearch={async () => {
          const term = (searchTerm || "").trim();
          if (term.length >= 4) {
            const added = await tryAddProductByBarcode(term);
            if (added) return;
          }

          dispatch(productActions.findAll({
            search: buildSearchQuery(term, searchMode),
            page: 0,
            size: 200,
          }));
          setCurrentPage(1);
        }}
        title={`Recherche Article (${currentSearchModeLabel})`}
      />

      {/* CART NUMERIC KEYPAD (QTY / PRICE) */}
      <POSNumericKeypadModal
        isOpen={numPadOpen}
        toggle={() => setNumPadOpen(false)}
        title={numPadConfig.title}
        value={numPadConfig.value}
        allowDecimal={true}
        onConfirm={(val) => {
          const parsed = Number(val || 0);
          if (Number.isNaN(parsed)) return;

          handleInputChange(numPadConfig.index, {
            target: { name: numPadConfig.field, value: parsed },
          });
        }}
      />

      <POSNumericKeypadModal
        isOpen={keypadOpen}
        toggle={() => setKeypadOpen(false)}
        title={
          keypadTarget === "cash"
            ? "Montant Espèces"
            : "Montant carte"
        }
        value={
          keypadTarget === "cash"
            ? formik.values.montantPayeCash
            : formik.values.montantPayeCarte
        }
        allowDecimal={true}
        maxLength={10}
        onConfirm={handleKeypadConfirm}
      />

      {/* PAY KEYPAD */}
      <POSNumericKeypadModal
        isOpen={payKeypadOpen}
        toggle={() => setPayKeypadOpen(false)}
        title="Montant payé"
        value={formik.values.montantPaye}
        allowDecimal={true}
        onConfirm={(val) => {
          const amount = Number(val || 0);
          if (Number.isNaN(amount)) return;
          formik.setFieldValue("montantPaye", amount);
        }}
      />

      {/* CLIENT SELECT MODAL */}
      <ClientSelectModal
        isOpen={clientSelectModal}
        toggle={() => setClientSelectModal(false)}
        clients={clientOptions}
        selectedClient={selectedClient}
        onSelect={(c) => {
          if (!c || normalizeClientLabel(c.label) !== "PASSAGER") setSelectedClient(c);
        }}
        onAddNew={() => {
          setClientSelectModal(false);
          setShowClientModal(true);
        }}
      />

      <Modal
        isOpen={showClientModal}
        toggle={() => setShowClientModal(false)}
        size="xl"
        centered
        className="pos-client-modal"
      >
        <ModalHeader
          toggle={() => setShowClientModal(false)}
          className="border-0 pb-0"
        >
          <div className="d-flex flex-column">
            <div className="fw-bold fs-4">{t("client.newClient")}</div>
            <small className="text-muted">Ajoutez rapidement un client</small>
          </div>
        </ModalHeader>

        <ModalBody className="pt-2">
          <Card className="border-0 shadow-sm rounded-4">
            <CardBody className="p-3">
              {/* TYPE CLIENT POS TOGGLE */}
              <Row className="g-3 mb-2">
                <Col md={12}>
                  <Label className="fw-bold text-muted mb-2">
                    <FaIdCard className="me-2" />
                    Type de client *
                  </Label>

                  <div className="d-flex gap-2 flex-wrap">
                    <Button
                      color={client.typeClient === "PARTICULIER" ? "primary" : "light"}
                      className="fw-bold px-4 py-2 rounded-3 border"
                      onClick={() =>
                        setClient((prev) => ({ ...prev, typeClient: "PARTICULIER" }))
                      }
                    >
                      Particulier
                    </Button>

                    <Button
                      color={client.typeClient === "ENTREPRISE" ? "primary" : "light"}
                      className="fw-bold px-4 py-2 rounded-3 border"
                      onClick={() =>
                        setClient((prev) => ({ ...prev, typeClient: "ENTREPRISE" }))
                      }
                    >
                      Entreprise
                    </Button>
                  </div>
                </Col>
              </Row>

              {/* DYNAMIC FIELDS */}
              {client.typeClient === "PARTICULIER" && (
                <Row className="g-3">
                  <Col md={4}>
                    <Label className="fw-bold">
                      <FaUser className="me-2 text-muted" />
                      Nom *
                    </Label>
                    <Input
                      name="nom"
                      value={client.nom}
                      onChange={handleClientChange}
                      placeholder="El Fassi"
                      invalid={!!clientErrors.nom}
                      className="py-2 rounded-3"
                    />
                    {clientErrors.nom && (
                      <div className="text-danger small mt-1">{clientErrors.nom}</div>
                    )}
                  </Col>

                  <Col md={4}>
                    <Label className="fw-bold">
                      <FaUser className="me-2 text-muted" />
                      Prénom *
                    </Label>
                    <Input
                      name="prenom"
                      value={client.prenom}
                      onChange={handleClientChange}
                      placeholder="Karim"
                      invalid={!!clientErrors.prenom}
                      className="py-2 rounded-3"
                    />
                    {clientErrors.prenom && (
                      <div className="text-danger small mt-1">{clientErrors.prenom}</div>
                    )}
                  </Col>

                  <Col md={4}>
                    <Label className="fw-bold">
                      <FaIdCard className="me-2 text-muted" />
                      CIN
                    </Label>
                    <Input
                      name="cin"
                      value={client.cin}
                      onChange={handleClientChange}
                      placeholder="AB123456"
                      className="py-2 rounded-3"
                    />
                  </Col>
                </Row>
              )}

              {client.typeClient === "ENTREPRISE" && (
                <Row className="g-3">
                  <Col md={6}>
                    <Label className="fw-bold">
                      <FaBuilding className="me-2 text-muted" />
                      Entreprise *
                    </Label>
                    <Input
                      name="entreprise"
                      value={client.entreprise}
                      onChange={handleClientChange}
                      placeholder="Nom de l'entreprise"
                      invalid={!!clientErrors.entreprise}
                      className="py-2 rounded-3"
                    />
                    {clientErrors.entreprise && (
                      <div className="text-danger small mt-1">{clientErrors.entreprise}</div>
                    )}
                  </Col>

                  <Col md={6}>
                    <Label className="fw-bold">
                      <FaIdCard className="me-2 text-muted" />
                      ICE *
                    </Label>
                    <Input
                      name="ice"
                      value={client.ice}
                      onChange={handleClientChange}
                      placeholder="Numéro ICE"
                      invalid={!!clientErrors.ice}
                      className="py-2 rounded-3"
                    />
                    {clientErrors.ice && (
                      <div className="text-danger small mt-1">{clientErrors.ice}</div>
                    )}
                  </Col>
                </Row>
              )}

              <hr className="my-3" />

              {/* COMMON FIELDS */}
              <Row className="g-3">
                <Col md={4}>
                  <Label className="fw-bold">
                    <FaPhone className="me-2 text-muted" />
                    Téléphone (1) *
                  </Label>
                  <Input
                    name="telephone1"
                    value={client.telephone1}
                    onChange={handleClientChange}
                    placeholder="06 12 34 56 78"
                    invalid={!!clientErrors.telephone1}
                    className="py-2 rounded-3"
                  />
                  {clientErrors.telephone1 && (
                    <div className="text-danger small mt-1">{clientErrors.telephone1}</div>
                  )}
                </Col>

                <Col md={4}>
                  <Label className="fw-bold">
                    <FaPhone className="me-2 text-muted" />
                    Téléphone (2)
                  </Label>
                  <Input
                    name="telephone2"
                    value={client.telephone2}
                    onChange={handleClientChange}
                    placeholder="06 12 34 56 78"
                    className="py-2 rounded-3"
                  />
                </Col>

                <Col md={4}>
                  <Label className="fw-bold">
                    <FaEnvelope className="me-2 text-muted" />
                    Email
                  </Label>
                  <Input
                    name="email"
                    value={client.email}
                    onChange={handleClientChange}
                    placeholder="k.elfassi@example.com"
                    className="py-2 rounded-3"
                  />
                </Col>
              </Row>

              <Row className="g-3 mt-1">
                <Col md={6}>
                  <Label className="fw-bold">
                    <FaMapMarkerAlt className="me-2 text-muted" />
                    Adresse *
                  </Label>
                  <Input
                    type="textarea"
                    rows="2"
                    name="adresse"
                    value={client.adresse}
                    onChange={handleClientChange}
                    invalid={!!clientErrors.adresse}
                    placeholder="12 Rue Moulay Rachid"
                    className="py-2 rounded-3"
                  />
                  {clientErrors.adresse && (
                    <div className="text-danger small mt-1">{clientErrors.adresse}</div>
                  )}
                </Col>

                <Col md={3}>
                  <Label className="fw-bold">
                    <FaMapMarkerAlt className="me-2 text-muted" />
                    Ville
                  </Label>

                  {/* ✅ POS STYLE CITY PICKER */}
                  <Input
                    name="ville"
                    value={client.ville}
                    onChange={handleClientChange}
                    className="py-2 rounded-3"
                    placeholder="Casablanca"
                  />
                  <small className="text-muted">
                    (Vous pouvez saisir directement la ville)
                  </small>
                </Col>

                <Col md={3}>
                  <Label className="fw-bold">
                    <FaMapMarkerAlt className="me-2 text-muted" />
                    Code Postal
                  </Label>
                  <Input
                    name="codePostal"
                    value={client.codePostal}
                    onChange={handleClientChange}
                    placeholder="20000"
                    className="py-2 rounded-3"
                  />
                </Col>
              </Row>
            </CardBody>
          </Card>
        </ModalBody>

        {/* POS FOOTER */}
        <ModalFooter className="border-0 d-flex justify-content-end gap-2">
          <Button
            color="secondary"
            outline
            className="fw-bold px-4 py-2 rounded-3"
            onClick={() => setShowClientModal(false)}
          >
            {t("common.cancel")}
          </Button>

          <Button
            color="primary"
            className="fw-bold px-4 py-2 rounded-3"
            onClick={handleSubmitClient}
          >
            {t("common.save")}
          </Button>
        </ModalFooter>
      </Modal>


    </Container>
  );
};

export default SaleForm;
